import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  cancelOrder,
  editOrderItems,
  getAdminOrder,
  getPaymentLink,
  markPaid,
  sendPaymentLink,
  setTrackingNumber,
  unmergeOrder,
  type AdminOrderRow,
  type OrderItemEdit,
  type UndeliveredEmail,
} from '../api/orders';
import { api } from '../api/client';
import { listProducts, type Product } from '../api/products';
import { ModalFoot, ModalHead, ModalShell } from './MergeModal';
import { isPlaceholderEmail } from '../util/placeholderEmail';
import { StatusPill, statusLabel } from '../components/StatusPill';
import type { OrderStatus, ShirtCut, Size } from '../types';
import { SHIPPING_LABEL } from '../types';
import { formatGroszeAsZl } from '../util/money';
import { formatPaczkomat } from '../util/paczkomat';

type ActivityRow = {
  id: number;
  at: string;
  operatorId: number | null;
  action: string;
  reason: string | null;
  meta: Record<string, unknown>;
};

// Keys are the raw `audit_log.action` values written by the API.
const ACTIVITY_LABEL: Record<string, string> = {
  'order.create': 'Zamówienie złożone',
  'order.create_manual': 'Zamówienie dodane ręcznie',
  'order.mark_paid': 'Oznaczone jako opłacone',
  'order.payment_amount_mismatch': 'Niezgodna kwota płatności',
  'order.cancel': 'Zamówienie anulowane',
  'order.designate_pickup': 'Spakowane do odbioru osobistego',
  'order.tracking_number_set': 'Wpisano numer przesyłki',
  'order.ship': 'Nadane przez InPost',
  'order.ship_manual': 'Nadane z ręcznym numerem',
  'order.ship_own_label': 'Spakowane i nadane za granicę',
  'order.merge_received': 'Dołączono zamówienie',
  'order.merged_into': 'Połączone z innym zamówieniem',
  'order.unmerge_received': 'Odłączono zamówienie',
  'order.unmerged': 'Rozłączone z zamówieniem nadrzędnym',
  'notification.sent': 'Powiadomienie wysłane',
  'notification.failed': 'Powiadomienie niewysłane',
  'notification.bounced': 'E-mail niedostarczony',
  'order.payment_link_sent': 'Wysłano link do płatności',
  'order.items_edited': 'Zmieniono koszulki',
};

// Keys are the `meta.template` values passed to dispatch() in apps/api/src/notifications/index.ts.
const NOTIFICATION_TEMPLATE_LABEL: Record<string, string> = {
  awaiting_payment: 'oczekiwanie na płatność',
  paid: 'potwierdzenie płatności',
  shipped: 'wysyłka',
  pickup: 'odbiór osobisty',
  cancelled: 'anulowanie',
};

function bounceReason(b: UndeliveredEmail): string {
  if (b.bounceSubType === 'OnAccountSuppressionList' || b.bounceSubType === 'Suppressed') {
    return 'adres jest zablokowany po wcześniejszym odrzuceniu, kolejne maile nie są wysyłane';
  }
  if (b.bounceType === 'Transient') return 'skrzynka chwilowo nie przyjmuje poczty (np. jest pełna)';
  return 'serwer odbiorcy odrzucił wiadomość, adres najpewniej nie istnieje';
}

function UndeliveredEmailAlert({ bounce, phone }: { bounce: UndeliveredEmail; phone: string }) {
  const template = bounce.template ? (NOTIFICATION_TEMPLATE_LABEL[bounce.template] ?? bounce.template) : null;
  const at = new Date(bounce.at);
  return (
    <div
      role="alert"
      style={{
        marginTop: 10,
        padding: '8px 10px',
        border: '1px solid var(--admin-danger)',
        borderRadius: 6,
        color: 'var(--admin-danger)',
        fontSize: 12,
      }}
    >
      <b>E-mail nie dociera do klienta.</b> {bounce.recipient}: {bounceReason(bounce)}
      {template && ` (ostatnio: ${template}, ${fmtDate(at)} ${fmtTime(at)})`}. Klient nie dostaje
      maili o zamówieniu; skontaktuj się telefonicznie: <span className="mono">{phone}</span>
    </div>
  );
}

type ItemLine = { productId: string; cut: string; size: string };
type ItemChange = { from: ItemLine; to: ItemLine };

const fmtLine = (l: ItemLine) => `${l.productId} · ${l.cut} · ${l.size}`;

function activityDetail(a: ActivityRow): string | null {
  const changes = a.meta['changes'];
  if (Array.isArray(changes)) {
    const summary = (changes as ItemChange[])
      .map((ch) => `${fmtLine(ch.from)} → ${fmtLine(ch.to)}`)
      .join('; ');
    return a.reason ? `${summary} · ${a.reason}` : summary;
  }
  if (a.reason) return a.reason;
  const template = a.meta['template'];
  if (typeof template === 'string') return NOTIFICATION_TEMPLATE_LABEL[template] ?? template;
  const recipient = a.meta['recipient'];
  if (typeof recipient === 'string') return recipient;
  const tracking = a.meta['tracking'];
  if (typeof tracking === 'string') return tracking;
  return null;
}

export function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState<AdminOrderRow | null>(null);
  const [items, setItems] = useState<AdminOrderRow['items']>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const [editItemsOpen, setEditItemsOpen] = useState(false);
  const reload = async () => {
    if (!id) return;
    try {
      const r = await getAdminOrder(id);
      setOrder(r.order);
      setItems(r.items);
      const act = await api.get<{ activity: ActivityRow[] }>(`/admin/orders/${id}/activity`);
      setActivity(act.activity);
    } catch {
      setOrder(null);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onMarkPaid = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await markPaid(id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async (reason: string, refundNote: string) => {
    if (!id) return;
    setBusy(true);
    try {
      await cancelOrder(id, reason, refundNote);
      setCancelModalOpen(false);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const onSetTrackingNumber = async (trackingNumber: string) => {
    if (!id) return;
    setBusy(true);
    try {
      await setTrackingNumber(id, trackingNumber);
      setTrackingModalOpen(false);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const onUnmerge = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await unmergeOrder(id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (!order) return <div className="muted">Ładuję…</div>;

  const created = new Date(order.createdAt);

  const canMarkPaid = order.status === 'awaiting_payment';
  const canSendPaymentLink = order.status === 'awaiting_payment';
  // A foreign parcel's carrier is booked by hand; its number is what sends the order to the kiosk,
  // which packs it under the shop's own label and marks it shipped.
  const canSetTrackingNumber = order.status === 'paid' && order.shippingMethod === 'zagranica';
  const canUnmerge = order.mergedIntoId !== null && order.status === 'merged';
  const canCancel = order.status !== 'shipped' && order.status !== 'cancelled' && order.status !== 'merged';
  // Once packed for pickup or shipped, the box contents are on a label. A merged-in order is
  // editable while its main is unpacked; the API checks that.
  const canEditItems =
    items.length > 0 && ['awaiting_payment', 'paid', 'merged'].includes(order.status);
  const hasOperations =
    canEditItems ||
    canMarkPaid ||
    canSendPaymentLink ||
    canSetTrackingNumber ||
    canUnmerge ||
    canCancel;

  return (
    <div className="order-detail">
      <div className="order-detail-col">
        <div className="card od-head" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
                ZAMÓWIENIE · TENERYFA 2026
              </div>
              <h1 style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                {order.id} <StatusPill status={(order.effectiveStatus ?? order.status) as OrderStatus} />
                {order.status === 'merged' && order.mergedIntoId && (
                  <span className="mono" style={{ fontSize: 13, color: 'var(--admin-muted)' }}>
                    ↔ w paczce {order.mergedIntoId}
                  </span>
                )}
              </h1>
              <div style={{ marginTop: 6, fontSize: 13, color: 'var(--admin-muted)' }} className="mono">
                Złożone {fmtDate(created)} · {fmtTime(created)}
              </div>
            </div>
          </div>
        </div>

        <div className="card od-items" style={{ padding: 18 }}>
          <h2>Pozycje ({items.length})</h2>
          <table>
            <thead>
              <tr>
                <th>Produkt · krój · rozmiar</th>
                <th>Ilość</th>
                <th className="c-unit" style={{ textAlign: 'right' }}>Cena</th>
                <th style={{ textAlign: 'right' }}>Suma</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={`${item.productId}-${item.cut}-${item.size}-${idx}`}>
                  <td>
                    <b>{item.productId} · {item.cut} · {item.size}</b>
                  </td>
                  <td>{item.qty}</td>
                  <td className="mono c-unit" style={{ textAlign: 'right' }}>
                    {formatGroszeAsZl(item.unitPriceGrosze)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatGroszeAsZl(item.unitPriceGrosze * item.qty)}
                  </td>
                </tr>
              ))}
              <tr>
                {/* The label stops short of the price column so that column can be dropped on a
                    phone without shifting the totals out from under the Suma header. */}
                <td colSpan={2} style={{ color: 'var(--admin-muted)' }}>
                  {SHIPPING_LABEL[order.shippingMethod]}
                </td>
                <td className="c-unit" />
                <td className="mono" style={{ textAlign: 'right' }}>
                  {formatGroszeAsZl(order.shippingCostGrosze)}
                </td>
              </tr>
              <tr style={{ background: '#FAF8F3' }}>
                <td colSpan={2} style={{ fontFamily: 'var(--display)', textTransform: 'uppercase', fontSize: 14 }}>
                  Razem
                </td>
                <td className="c-unit" />
                <td
                  className="mono"
                  style={{
                    textAlign: 'right',
                    fontWeight: 700,
                    fontFamily: 'var(--display)',
                    fontSize: 18,
                    color: 'var(--orange-deep)',
                  }}
                >
                  {formatGroszeAsZl(order.totalGrosze)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {order.customerCommentText && (
          <div className="card od-comment" style={{ padding: 18 }}>
            <h2>
              Komentarz klienta{' '}
              {order.customerCommentTopic && (
                <span style={{ fontFamily: 'var(--body)', textTransform: 'none', color: 'var(--admin-muted)', fontSize: 13 }}>
                  · {order.customerCommentTopic}
                </span>
              )}
            </h2>
            <div
              style={{
                background: '#FFFAF0',
                border: '1px solid #F0D9B8',
                padding: 12,
                fontSize: 13,
                lineHeight: 1.5,
                marginTop: 8,
              }}
            >
              "{order.customerCommentText}"
            </div>
          </div>
        )}

        <div className="card od-activity" style={{ padding: 18 }}>
          <h2>Aktywność</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, fontSize: 13 }}>
            {activity.length === 0 && <span className="muted">Brak zdarzeń.</span>}
            {activity.map((a) => {
              const detail = activityDetail(a);
              return (
                <div key={a.id} className="activity-row">
                  <span className="mono" style={{ color: 'var(--admin-muted)' }}>
                    {new Date(a.at).toLocaleString('pl-PL')}
                  </span>
                  <span style={{ flex: 1 }}>
                    <b>{ACTIVITY_LABEL[a.action] ?? a.action}</b>
                    {detail && <span style={{ color: 'var(--admin-muted)' }}> · {detail}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="order-detail-col">
        <div className="card od-client" style={{ padding: 18 }}>
          <h2>Klient</h2>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            <b>{order.customerName}</b>
          </div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 2 }}>
            {isPlaceholderEmail(order.email) ? <i>brak adresu email</i> : order.email}
            <br />
            {order.phone}
          </div>
          {order.externalOrderId && (
            <div className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 6 }}>
              Zam. zewnętrzne: {order.externalOrderId}
            </div>
          )}
          {order.undeliveredEmail && (
            <UndeliveredEmailAlert bounce={order.undeliveredEmail} phone={order.phone} />
          )}
        </div>

        <div className="card od-shipping" style={{ padding: 18 }}>
          <h2>Wysyłka</h2>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            <b>{SHIPPING_LABEL[order.shippingMethod]}</b>
          </div>
          <div className="mono" style={{ color: 'var(--admin-muted)', fontSize: 12, marginTop: 6 }}>
            {order.shippingMethod === 'paczkomat' && order.shippingAddress.paczkomat && (
              <>{formatPaczkomat(order.shippingAddress.paczkomat)}</>
            )}
            {order.shippingMethod === 'kurier' && (
              <>
                {order.shippingAddress.courier?.street}
                <br />
                {order.shippingAddress.courier?.postalCode} {order.shippingAddress.courier?.city}
              </>
            )}
            {order.shippingMethod === 'zagranica' && (
              <>
                {order.shippingAddress.international?.countryName ?? '— brak kraju —'}
                <br />
                Sposób i koszt wysyłki ustalane z kupującym — zapłacone są same koszulki.
              </>
            )}
            {order.shippingMethod === 'odbior' && (order.shippingAddress.pickupNote ?? 'Odbiór osobisty')}
          </div>
          {order.shipsWithId && (
            <div
              className="mono"
              style={{ fontSize: 12, marginTop: 8, color: 'var(--admin-muted)' }}
            >
              Jedzie w paczce {order.shipsWithId} · bez opłaty za wysyłkę. Połączenie powstanie, gdy
              obie płatności wpłyną — do tego czasu tamta paczka nie zostanie wysłana.
            </div>
          )}
          {order.trackingNumber && (
            <div className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 8 }}>
              Tracking: {order.trackingNumber}
            </div>
          )}
          {order.status === 'paid' && (
            <div className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 8 }}>
              {order.shippingMethod === 'zagranica' && !order.trackingNumber
                ? 'Trafi do logistyki, gdy wpiszesz numer przesyłki od przewoźnika.'
                : 'Czeka na spakowanie w logistyce.'}
            </div>
          )}
        </div>

        <div className="card od-ops" style={{ padding: 18 }}>
          <h2>Operacje</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
            {!hasOperations && (
              <span className="muted" style={{ fontSize: 13 }}>
                Brak operacji dostępnych dla statusu „{statusLabel(order.status)}”.
              </span>
            )}
            {canMarkPaid && (
              <button
                className="btn-a primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={onMarkPaid}
                disabled={busy}
              >
                ✓ Oznacz jako opłacone
              </button>
            )}
            {canSendPaymentLink && (
              <button
                className="btn-a"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setPaymentLinkOpen(true)}
                disabled={busy}
              >
                ✉ Link do płatności
              </button>
            )}
            {canEditItems && (
              <button
                className="btn-a"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setEditItemsOpen(true)}
                disabled={busy}
              >
                ✎ Zmień koszulki
              </button>
            )}
            {canSetTrackingNumber && (
              <button
                className="btn-a"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setTrackingModalOpen(true)}
                disabled={busy}
              >
                ✈ {order.trackingNumber ? 'Zmień numer przesyłki' : 'Wpisz numer przesyłki'}
              </button>
            )}
            {canUnmerge && (
              <button
                className="btn-a"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={onUnmerge}
                disabled={busy}
              >
                ⇄ Rozłącz z {order.mergedIntoId}
              </button>
            )}
            {canCancel && (
              <button
                className="btn-a danger"
                style={{ width: '100%', justifyContent: 'center', borderColor: '#E8C4C0' }}
                onClick={() => setCancelModalOpen(true)}
                disabled={busy}
              >
                ✕ Anuluj zamówienie
              </button>
            )}
          </div>
        </div>
      </div>

      {cancelModalOpen && (
        <CancelModal
          orderId={order.id}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={onCancel}
        />
      )}
      {trackingModalOpen && (
        <TrackingNumberModal
          orderId={order.id}
          destination={order.shippingAddress.international?.countryName ?? null}
          current={order.trackingNumber}
          onClose={() => setTrackingModalOpen(false)}
          onConfirm={onSetTrackingNumber}
        />
      )}
      {editItemsOpen && (
        <EditItemsModal
          orderId={order.id}
          items={items}
          onClose={() => setEditItemsOpen(false)}
          onSaved={async () => {
            setEditItemsOpen(false);
            await reload();
          }}
        />
      )}
      {paymentLinkOpen && (
        <PaymentLinkModal
          orderId={order.id}
          onClose={() => setPaymentLinkOpen(false)}
          onSent={reload}
        />
      )}
    </div>
  );
}

const CUTS: ShirtCut[] = ['męski', 'damski'];
const SIZES: Size[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

function editItemsError(err: unknown): string {
  const body = (err as { body?: { error?: string; status?: string; main_status?: string } }).body;
  switch (body?.error) {
    case 'not_editable':
      return `Zamówienia w statusie „${statusLabel(body.status ?? '')}” nie można już zmienić · odśwież stronę.`;
    case 'main_not_paid':
      return 'Paczka, do której dołączono to zamówienie, jest już spakowana lub wysłana.';
    case 'product_inactive':
      return 'Wybrany kolor nie jest już w sprzedaży · odśwież stronę.';
    case 'unknown_item':
      return 'Pozycje zamówienia zmieniły się w międzyczasie · odśwież stronę.';
    default:
      return 'Nie udało się zapisać zmian · odśwież stronę i spróbuj ponownie.';
  }
}

function EditItemsModal({
  orderId,
  items,
  onClose,
  onSaved,
}: {
  orderId: string;
  items: AdminOrderRow['items'];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [rows, setRows] = useState<OrderItemEdit[]>(() =>
    items.map((it) => ({ id: it.id, product_id: it.productId, cut: it.cut, size: it.size })),
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listProducts()
      .then((r) => setProducts(r.products))
      .catch(() => setProducts([]));
  }, []);

  const update = (id: number, patch: Partial<OrderItemEdit>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const changed = rows.some((r) => {
    const was = items.find((it) => it.id === r.id)!;
    return was.productId !== r.product_id || was.cut !== r.cut || was.size !== r.size;
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await editOrderItems(orderId, rows, reason.trim() || undefined);
      await onSaved();
    } catch (err) {
      setError(editItemsError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={640}>
      <ModalHead title="Zmień koszulki" subtitle={orderId} onClose={onClose} />
      <div style={{ padding: 22, fontSize: 13 }}>
        <p style={{ margin: 0, color: 'var(--admin-muted)' }}>
          Ilość i cena zostają bez zmian · bez dopłaty i zwrotu. Limit koszulek na bilet nie jest
          sprawdzany.
        </p>
        <table className="manual-items" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Kolor</th>
              <th>Krój</th>
              <th>Rozmiar</th>
              <th>Ilość</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const original = items.find((it) => it.id === row.id)!;
              // A withdrawn product is not in the catalogue, but the line may keep it.
              const keepsWithdrawn = !products.some((p) => p.id === original.productId);
              return (
                <tr key={row.id}>
                  <td className="c-color" data-label="Kolor">
                    <select
                      className="input-a"
                      aria-label="Kolor"
                      value={row.product_id}
                      onChange={(e) => update(row.id, { product_id: e.target.value })}
                    >
                      {keepsWithdrawn && <option value={original.productId}>{original.productId}</option>}
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.color}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="c-cut" data-label="Krój">
                    <select
                      className="input-a"
                      aria-label="Krój"
                      value={row.cut}
                      onChange={(e) => update(row.id, { cut: e.target.value as ShirtCut })}
                    >
                      {CUTS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="c-size" data-label="Rozmiar">
                    <select
                      className="input-a"
                      aria-label="Rozmiar"
                      value={row.size}
                      onChange={(e) => update(row.id, { size: e.target.value as Size })}
                    >
                      {SIZES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="c-qty mono" data-label="Ilość">
                    {original.qty}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="field" style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11 }} htmlFor="edit-items-reason">
            Powód (opcjonalny)
          </label>
          <textarea
            id="edit-items-reason"
            className="input-a"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        {error && <LinkAlert message={error} />}
      </div>
      <ModalFoot>
        <button className="btn-a" onClick={onClose} disabled={saving}>
          Zamknij
        </button>
        <button className="btn-a primary" onClick={save} disabled={saving || !changed}>
          Zapisz zmiany
        </button>
      </ModalFoot>
    </ModalShell>
  );
}

function paymentLinkError(err: unknown, fallback: string): string {
  const body = (err as { body?: { error?: string; reason?: string } }).body;
  switch (body?.error) {
    case 'stripe_unavailable':
      return 'Płatności online są wyłączone · link by nie zadziałał.';
    case 'not_awaiting_payment':
      return 'Zamówienie nie czeka już na płatność · odśwież stronę.';
    case 'no_email':
      return 'Zamówienie nie ma adresu email · skopiuj link i przekaż go klientowi.';
    case 'email_failed':
      return `Nie udało się wysłać maila${body.reason ? ` (${body.reason})` : ''} · skopiuj link i przekaż go klientowi.`;
    default:
      return fallback;
  }
}

function PaymentLinkModal({
  orderId,
  onClose,
  onSent,
}: {
  orderId: string;
  onClose: () => void;
  onSent: () => void | Promise<void>;
}) {
  const [link, setLink] = useState<{ url: string; email: string | null } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'ok' | 'manual' | null>(null);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    getPaymentLink(orderId)
      .then((r) => live && setLink(r))
      .catch(
        (err: unknown) =>
          live && setLoadError(paymentLinkError(err, 'Nie udało się przygotować linku do płatności.')),
      );
    return () => {
      live = false;
    };
  }, [orderId]);

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied('ok');
    } catch {
      // Clipboard access needs a secure context and permission; leave the text selected instead.
      urlRef.current?.select();
      setCopied('manual');
    }
  };

  const send = async () => {
    setSending(true);
    setSendError(null);
    setSentTo(null);
    try {
      const r = await sendPaymentLink(orderId);
      setSentTo(r.recipient);
      await onSent();
    } catch (err) {
      setSendError(
        paymentLinkError(err, 'Nie udało się wysłać maila · skopiuj link i przekaż go klientowi.'),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={560}>
      <ModalHead title="Link do płatności" subtitle={orderId} onClose={onClose} />
      <div style={{ padding: 22, fontSize: 13 }}>
        {!link && !loadError && <span className="muted">Przygotowuję link…</span>}
        {loadError && <LinkAlert message={loadError} />}
        {link && (
          <>
            <p style={{ margin: 0 }}>
              Link prowadzi do strony zamówienia, gdzie klient zapłaci online (BLIK, karta,
              Przelewy24).
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                ref={urlRef}
                className="input-a mono"
                aria-label="Link do płatności"
                readOnly
                value={link.url}
                onFocus={(e) => e.currentTarget.select()}
                style={{ flex: 1, fontSize: 12 }}
              />
              <button type="button" className="btn-a" onClick={copy}>
                {copied === 'ok' ? '✓ Skopiowano' : 'Kopiuj'}
              </button>
            </div>
            {copied === 'manual' && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--admin-muted)' }}>
                Nie mam dostępu do schowka · link jest zaznaczony, skopiuj go Ctrl+C / ⌘C.
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              {link.email ? (
                <>
                  Mail z linkiem trafi na <b>{link.email}</b>.
                </>
              ) : (
                <span style={{ color: 'var(--admin-muted)' }}>
                  Zamówienie nie ma adresu email · skopiuj link i przekaż go klientowi (SMS,
                  komunikator).
                </span>
              )}
            </div>
            {sentTo && (
              <div role="status" style={{ marginTop: 10, color: '#2F6B3A', fontWeight: 700 }}>
                ✓ Wysłano na {sentTo}
              </div>
            )}
            {sendError && <LinkAlert message={sendError} />}
          </>
        )}
      </div>
      <ModalFoot>
        <button className="btn-a" onClick={onClose}>
          Zamknij
        </button>
        {link?.email && (
          <button className="btn-a primary" onClick={send} disabled={sending}>
            {sentTo ? 'Wyślij ponownie' : 'Wyślij mailem'}
          </button>
        )}
      </ModalFoot>
    </ModalShell>
  );
}

function LinkAlert({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 10,
        padding: '8px 12px',
        border: '1px solid #C9483A',
        color: '#C9483A',
        fontSize: 12,
      }}
    >
      {message}
    </div>
  );
}

function CancelModal({
  orderId,
  onClose,
  onConfirm,
}: {
  orderId: string;
  onClose: () => void;
  onConfirm: (reason: string, refundNote: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [refundNote, setRefundNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--admin-card)',
          border: '1px solid var(--admin-line-strong)',
          padding: 24,
          width: 480,
          maxWidth: '90vw',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Anuluj zamówienie {orderId}</h2>
        <div className="field" style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11 }}>Powód (wymagany)</label>
          <textarea
            className="input-a"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div className="field" style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11 }}>Notatka o zwrocie środków (opcjonalna)</label>
          <textarea
            className="input-a"
            rows={2}
            value={refundNote}
            onChange={(e) => setRefundNote(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn-a" onClick={onClose} disabled={submitting}>
            Zamknij
          </button>
          <button
            className="btn-a danger"
            disabled={submitting || reason.trim().length === 0}
            onClick={async () => {
              setSubmitting(true);
              await onConfirm(reason.trim(), refundNote.trim());
              setSubmitting(false);
            }}
          >
            Anuluj zamówienie
          </button>
        </div>
      </div>
    </div>
  );
}

function TrackingNumberModal({
  orderId,
  destination,
  current,
  onClose,
  onConfirm,
}: {
  orderId: string;
  destination: string | null;
  current: string | null;
  onClose: () => void;
  onConfirm: (trackingNumber: string) => Promise<void>;
}) {
  const [trackingNumber, setTrackingNumber] = useState(current ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--admin-card)',
          border: '1px solid var(--admin-line-strong)',
          padding: 24,
          width: 480,
          maxWidth: '90vw',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Numer przesyłki {orderId}</h2>
        <p style={{ fontSize: 13, color: 'var(--admin-muted)', marginTop: 8 }}>
          Kraj docelowy: <b>{destination ?? '— brak —'}</b>. Wpisz numer od przewoźnika, z którym
          umówiona jest wysyłka. Z numerem zamówienie trafi do kolejki logistyki — po spakowaniu i
          wydruku etykiety zostanie oznaczone jako wysłane, a kupujący dostanie numer mailem.
        </p>
        <div className="field" style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11 }}>Numer przesyłki (wymagany)</label>
          <input
            className="input-a"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn-a" onClick={onClose} disabled={submitting}>
            Zamknij
          </button>
          <button
            className="btn-a primary"
            disabled={submitting || trackingNumber.trim().length === 0}
            onClick={async () => {
              setSubmitting(true);
              setError(false);
              try {
                await onConfirm(trackingNumber.trim());
              } catch {
                setError(true);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            Zapisz numer
          </button>
        </div>
        {error && <LinkAlert message="Nie udało się zapisać numeru · odśwież stronę i spróbuj ponownie." />}
      </div>
    </div>
  );
}

function fmtDate(d: Date) {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function fmtTime(d: Date) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
